import * as Sentry from '@sentry/react';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import { loggerUtils } from '../Logging';

import { getLaunchDarklyUserKeyCookieValue } from './internal';

export interface ILaunchDarklyUpdater {
  updateLaunchDarkly(): Promise<void>;
}

/**
 * useLaunchDarklyUpdater is likely not what you want to be using. It's meant to
 * be used within the guts of newAuthState.
 *
 * useLaunchDarklyUpdater handles tracking users as they move from anonymous to
 * signed-in and back in the the LaunchDarkly client. Calls to
 * {@link ILaunchDarklyUpdater.update} method on the returned object will check
 * for changes to the cookies returned by the server and adjust the local
 * identification of the user. No remote services are called for that.
 */
export const useLaunchDarklyUpdater = (): ILaunchDarklyUpdater => {
  const ldClient = useLDClient();
  if (!ldClient) {
    throw new Error(
      'useLaunchDarklyUpdateUser must be used within a ConfiguredFeatureFlagsProvider context'
    );
  }

  const updateLaunchDarkly = async (): Promise<void> => {
    const newLDUserKey = getLaunchDarklyUserKeyCookieValue();
    const currentLdUserKey = ldClient.getUser().key;
    if (newLDUserKey === currentLdUserKey) {
      return;
    }
    const user = newLDUserKey ? { key: newLDUserKey } : { anonymous: true };

    try {
      await ldClient.identify(user);
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag('subsystem', 'feature-flags');
        loggerUtils.logException(error as Error);
      });
    }
  };

  return { updateLaunchDarkly };
};
